import React, {useState} from 'react';
import {
    Button,
    Space,
    Typography,
    Modal,
    Alert,
    Descriptions,
    Tag,
    Select,
    message,
    Divider
} from 'antd';
import {
    ExclamationCircleOutlined,
    IdcardOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import {internalApi} from '../../common/axios';
import PlayerInfoSearch from "./PlayerInfoSearch";
import PlayerInfo from "./PlayerInfo";
import {defaultPlayerData} from "../../shared/contexts/playerContext";

const {Text} = Typography;

const dbOptions = [
    {label: '라이브', value: 'production'},
    {label: 'QA', value: 'qa'},
    {label: '개발', value: 'develop'},
];

const PlayerTransferAccount = () => {
    const [db, setDB] = useState('production');
    const [playerLeft, setPlayerLeft] = useState(defaultPlayerData);
    const [playerRight, setPlayerRight] = useState(defaultPlayerData);
    const [modalOpen, setModalOpen] = useState(false);
    const [transferLoading, setTransferLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // 계정 이전 실행
    const handleTransfer = async () => {
        setTransferLoading(true);
        try {
            const res = await internalApi.post('/player/transfer', {
                fromUserKey: playerLeft.userKey,
                toUserKey: playerRight.userKey,
                db,
            });
            message.success(res.data.message || '계정 이전 성공');
            setModalOpen(false);
        } catch (e) {
            message.error(e.response?.data?.message || '계정 이전 실패');
        } finally {
            setTransferLoading(false);
        }
    };

    return (
        <div style={{width: '90%', margin: '0 auto', padding: 24}}>
            <Typography.Title level={3} style={{textAlign: 'center', marginBottom: 32}}>
                계정 이전
            </Typography.Title>
            <div style={{textAlign: 'center', marginBottom: 24}}>
                <Select options={dbOptions} value={db} onChange={setDB} style={{width: 180}} disabled={isSearching}/>
            </div>
            <Space align="start" size={32} style={{width: '100%', justifyContent: 'center'}}>
                {/* 기존 계정 */}
                <div style={{
                    minWidth: 1000,
                    background: 'var(--card-background)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 8,
                    padding: 16
                }}>
                    <Text strong>이전 할 계정</Text>
                    <PlayerInfoSearch db={db} setPlayerData={setPlayerLeft} searchChecker={setIsSearching}/>
                    <Divider style={{margin: '24px 0', borderColor: 'var(--border-color)'}}/>
                    <PlayerInfo playerData={playerLeft} setPlayerData={setPlayerLeft} isCopy={true}/>
                </div>
                {/* 이전할 계정 */}
                <div style={{
                    minWidth: 1000,
                    background: 'var(--card-background)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 8,
                    padding: 16
                }}>
                    <Text strong>타겟 계정</Text>
                    <PlayerInfoSearch db={db} setPlayerData={setPlayerRight} searchChecker={setIsSearching}/>
                    <Divider style={{margin: '24px 0', borderColor: 'var(--border-color)'}}/>
                    <PlayerInfo playerData={playerRight} setPlayerData={setPlayerRight} isCopy={true}/>
                </div>
            </Space>
            {/* 이전 실행 버튼 */}
            <div style={{textAlign: 'center', marginTop: 32}}>
                <Button
                    type="primary"
                    icon={<ArrowRightOutlined/>}
                    size="large"
                    disabled={!playerLeft || !playerRight}
                    onClick={() => {
                        if (!playerLeft?.userKey || playerLeft.userKey === 0 || !playerRight?.userKey || playerRight.userKey === 0) {
                            message.warning('양쪽 계정의 UserKey를 모두 검색해 주세요.');
                            return;
                        }
                        if (playerLeft.userKey === playerRight.userKey) {
                            message.warning('두 계정의 UserKey가 동일합니다. 서로 다른 계정을 선택해 주세요.');
                            return;
                        }
                        setModalOpen(true);
                    }}
                >
                    계정 이전 실행
                </Button>
            </div>
            {/* 확인 모달 */}
            <Modal
                title={<Space align="center"><ExclamationCircleOutlined style={{color: '#faad14', fontSize: 20}}/><Text
                    strong>계정 이전 확인</Text></Space>}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                width={600}
                footer={[
                    <Button key="cancel" size="large" onClick={() => setModalOpen(false)}>취소</Button>,
                    <Button key="confirm" type="primary" size="large" loading={transferLoading}
                            onClick={handleTransfer}>
                        이전 실행
                    </Button>
                ]}
            >
                <Space direction="vertical" size="large" style={{width: '100%'}}>
                    <Alert message="정말로 계정 이전을 실행하시겠습니까?" type="info" showIcon/>
                    <div className="info-section">
                        <div className="section-header">
                            <IdcardOutlined className="section-icon"/>
                            <Text strong className="section-title">이전될 계정 정보</Text>
                        </div>
                        <Descriptions variant="outlined" column={1} size="small" className="info-descriptions">
                            <Descriptions.Item
                                label="기존 PID">{playerLeft?.playerInfo?.userInfo?.Pid || '-'}</Descriptions.Item>
                            <Descriptions.Item label="기존 UserKey"><Text
                                code>{playerLeft?.userKey}</Text></Descriptions.Item>
                            <Descriptions.Item
                                label="이전할 PID">{playerRight?.playerInfo?.userInfo?.Pid || '-'}</Descriptions.Item>
                            <Descriptions.Item label="이전할 UserKey"><Text
                                code>{playerRight?.userKey}</Text></Descriptions.Item>
                            <Descriptions.Item label="DB"><Tag color="blue">{db}</Tag></Descriptions.Item>
                        </Descriptions>
                    </div>
                    <Alert message="주의: 이전할 계정이 실제로 일치하는지 확인 후 진행하세요." type="warning" showIcon
                           icon={<ExclamationCircleOutlined/>}/>
                </Space>
            </Modal>
        </div>
    );
};

export default PlayerTransferAccount;
